import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { AppThunk, RootState } from './index';
import { makeWithdrawalResponse, ModalTransaction, PaymentCreation } from '../types/transactions';
import { PaymentHistoryData, PaymentService, PaymentsHistory } from '../services/PaymentService';
import { setModalSignInUp } from './AppSlice';
import { modalTypeEnum } from '../types/modal';
import { errors } from '../types/errors';
import { setWithdrawalStatus } from './LoaderSlice';

type PurchaseSliceType = {
    createPurchaseValues: {
        value: number
    },
    modalTransactionStatus: ModalTransaction,
    paymentCompleted: boolean,
    inPayment: boolean,
    listTransactions: PaymentHistoryData[] | [],
    transactionsHistory: PaymentsHistory,
    cursorLoading: boolean,
    quotationInfo: {},
    PaymentCreationInfo: {},
    paymentCreationModalError: string,
    paymentChargeInfo: {},
    paymentChargeLoader: boolean,
    withdrawalPreview: {},
    currentWorkingPair: string,
    makeWithdrawalStatus: number | null,
    currentCursor: string,
    availableTokens: Array<string>,
    withdrawalDone: boolean
}

const initialState: PurchaseSliceType = {
    createPurchaseValues: {
        value: 0
    },
    modalTransactionStatus: {
        status: 'not-started',
        res: 'success',
        msg: ''
    },
    paymentCompleted: false,
    inPayment: false,
    listTransactions: [],
    transactionsHistory: {
        data: [],
        has_next: true,
        next_cursor: "",
        prev_cursor: "",
        success: false,
    },
    cursorLoading: false,
    quotationInfo: {},
    PaymentCreationInfo: {},
    paymentCreationModalError: '',
    paymentChargeInfo: {},
    paymentChargeLoader: false,
    withdrawalPreview: {},
    currentWorkingPair: '',
    makeWithdrawalStatus: null,
    currentCursor: '',
    availableTokens:[],
    withdrawalDone: false
};

const transactionSlice = createSlice({
    name: 'transaction',
    initialState,
    reducers: {
        setPurchase: (state, action) => {
            state.createPurchaseValues = action.payload;
        },
        setTransactiopnModalStatus: (state, action) => {
            state.modalTransactionStatus.status = action.payload;
        },
        setPaymentCompleted: (state, action) => {
            state.paymentCompleted = action.payload;
        },
        setTransactionsHistory: (state, action) => {
            state.transactionsHistory = action.payload;
        },
        setCursorLoading: (state, action) => {
            state.cursorLoading = action.payload;
        },
        setQuotations: (state, action) => {
            state.quotationInfo = action.payload;
        },
        setPaymentCreationInfo: (state, action) => {
            state.PaymentCreationInfo = action.payload;
        },
        setPaymentCreationError: (state, action) => {
            state.paymentCreationModalError = action.payload;
        },
        setPaymentChargeInfo: (state, action) => {
            state.paymentChargeInfo = action.payload;
        },
        setPaymentChargeLoader: (state, action) => {
            state.paymentChargeLoader = action.payload;
        },
        setPreview: (state, action) => {
            state.withdrawalPreview = action.payload;
        },
        setInPayment: (state, action: PayloadAction<boolean>) => {
            state.inPayment = action.payload;
        },
        addListTransactions: (state, action: PayloadAction<PaymentHistoryData[]>) => {
            state.listTransactions = [...state.listTransactions, ...action.payload]
        },
        setCurrentWorkingPair: (state, action: PayloadAction<string>) => {
            state.currentWorkingPair = action.payload;
        },
        cleanListTransactions: (state) => {
            state.transactionsHistory = {
                data: [],
                has_next: true,
                next_cursor: "",
                prev_cursor: "",
                success: false,
            };
            state.listTransactions = [];
        },
        setMakewithdrawalStatus: (state, action: PayloadAction<number>) => {
            state.makeWithdrawalStatus = action.payload;
        },
        setCurrentCursor: (state, action: PayloadAction<string>) => {
            state.currentWorkingPair = action.payload;
        },
        setAvaialbleTokenList: (state, action: PayloadAction<Array<string>>) => {
            state.availableTokens = action.payload;
        },
        setWithdrawalIsDone: (state, action: PayloadAction<boolean>) => {
            state.withdrawalDone = action.payload;
        },
    }
});

// EXPORT DEFAULT
export const {
    addListTransactions,
    cleanListTransactions,
    setPurchase,
    setTransactionsHistory,
    setTransactiopnModalStatus,
    setCursorLoading,
    setQuotations,
    setPaymentCreationInfo,
    setPaymentCreationError,
    setPaymentChargeInfo,
    setPaymentChargeLoader,
    setPaymentCompleted,
    setPreview,
    setCurrentWorkingPair,
    setInPayment,
    setMakewithdrawalStatus,
    setAvaialbleTokenList,
    setCurrentCursor,
    setWithdrawalIsDone } = transactionSlice.actions
export default transactionSlice.reducer;

export const getPurchase = (state: RootState) => state.transaction.createPurchaseValues;
export const getPaymentCompleted = (state: RootState): boolean => state.transaction.paymentCompleted;
export const getInPayment = (state: RootState): boolean => state.transaction.inPayment;
export const getModalTransactionStatus = (state: RootState): ModalTransaction => state.transaction.modalTransactionStatus;
export const getTransactionsHistory = (state: RootState): any => state.transaction.transactionsHistory;
export const getListTransactions = (state: RootState): any => state.transaction.listTransactions;
export const getCursorLoading = (state: RootState): any => state.transaction.cursorLoading
export const getQuotationInfo = (state: RootState): any => state.transaction.quotationInfo
export const getPaymentCreationInfo = (state: RootState): any => state.transaction.PaymentCreationInfo
export const getPaymentChargeInfo = (state: RootState): any => state.transaction.paymentChargeInfo
export const getPaymentChargeLoader = (state: RootState): any => state.transaction.paymentChargeLoader
export const getWithdrawalPreview = (state: RootState): any => state.transaction.withdrawalPreview;
export const getCurrentWorkingPair = (state: RootState): any => state.transaction.currentWorkingPair;
export const getMakewithdrawalStatus = (state: RootState): any => state.transaction.makeWithdrawalStatus;
export const getPaymentCreationError = (state: RootState): any => state.transaction.paymentCreationModalError;
export const getAvailable = (state: RootState): any => state.transaction.availableTokens;
export const getWithdrawalIsDone = (state: RootState): any => state.transaction.withdrawalDone;

export const getUserTransactionHistory = (limit: number = 40, direction: string, cursor?: string): AppThunk<Promise<void>> => async (dispatch, getState) => {

    const state = getState();
    let clear = false;

    const lastCursor = state.transaction.currentCursor;
    const lastFilter = state.transaction.transactionsHistory;

    const sort = direction ? direction : 'created_at:desc';
    const filter = { cursor: lastFilter?.next_cursor, limit, sort }

    if (cursor) {
        filter.cursor =  cursor;
        clear = true;
    }

    if (!lastCursor || lastCursor !== filter.cursor) {
        await dispatch(getActivities(filter, clear));
    }
    
}

const getActivities = (filter:any, clear: boolean): AppThunk<Promise<void>> => async dispatch => {

    dispatch(setCursorLoading(true));
    try {
        const data = await PaymentService.getUserActivities(filter);
        if (clear) dispatch( cleanListTransactions() );
        dispatch(setTransactionsHistory(data));
        const filtered = data.data.filter((item: any) => item.event !== undefined);
        if (filtered.length > 0) dispatch(addListTransactions(filtered));
    } catch (err:any) {
        console.dir(err.message);
        const timer = setTimeout( () => {
            getActivities(filter, clear);
            clearTimeout(timer);
        },3000)
    } finally {
        dispatch(setCursorLoading(false));
    }
}


export const getQuotations = (pair: string): AppThunk<Promise<void>> => async (dispatch, getState) => {
    const fee = getState().user.userFee
    const nposUnitFee = getState().user.nposUnitFee
    const pairInfo = await PaymentService.getQuotations(pair, fee, nposUnitFee);
    if (pairInfo) dispatch(setQuotations(pairInfo))
}

export const createPayment = (payload: PaymentCreation): AppThunk<Promise<void>> => async (dispatch) => {
    if (payload.quote_amount > 0 || payload.amount > 0) {
        const paymentCreation = await PaymentService.createPayment(payload);
        if (paymentCreation.code === 200 || paymentCreation.code === 201) {
            dispatch(setPaymentCreationInfo(paymentCreation.data));
        } else {
            if (paymentCreation.status === 400) {    
                let errorMessage = errors(paymentCreation.response.data.code);
                if (errorMessage === '') errorMessage = paymentCreation.response.data;
                dispatch(setPaymentCreationError(errorMessage));
            } else {
                dispatch(setPaymentCreationError(paymentCreation.message));
            }
            dispatch(setPaymentChargeLoader(false));
        }
    }
}

export const getPaymentCharge = (chargeId: string): AppThunk<Promise<void>> => async (dispatch) => {
    const paymentCreation = await PaymentService.getPaymentCharge(chargeId);
    if (paymentCreation.code === 200 || paymentCreation.code === 201) dispatch(setPaymentChargeInfo(paymentCreation.data));
    else dispatch(setPaymentCreationError(paymentCreation.message));
}

export const cancelPurchase = (paymentChargeInfo: any): AppThunk<Promise<void>> => async (dispatch) => {
    // cancel payment info
}

//withdrawal process
export const requestWithdrawalPreview = (pair:string): AppThunk<Promise<void>> => async (dispatch) => {

    if(!pair) pair = 'USDT:fiatBRL';

    try {
        const withdrawal : any = await PaymentService.getPreview(pair);
        if (withdrawal && (withdrawal.code === 201 || withdrawal.code === 200)) {
            dispatch(setPreview(withdrawal))
        } else {
            dispatch(setPreview(withdrawal.response))
        }
    } catch (err) {
        console.error('Error getting preview')
        dispatch(setModalSignInUp(modalTypeEnum.deviceLogin));
    }

}

export const makeWithdrawal = (): AppThunk<Promise<void>> => async (dispatch, getState) => {
    
    const state         = getState();
    const pair          = state.transaction.currentWorkingPair;

    dispatch(setWithdrawalIsDone(true));

    try {
        const makewithdrawal : any | makeWithdrawalResponse = await PaymentService.makeWithdrawalRequest({ pair });
        if (makewithdrawal && makewithdrawal.code === 201) {
            dispatch(setWithdrawalStatus(false));
            dispatch(setWithdrawalIsDone(false));
            dispatch(setMakewithdrawalStatus(Number(makewithdrawal.code)))
        } else {
            dispatch(setWithdrawalStatus(false));
            dispatch(setWithdrawalIsDone(false));
            dispatch(setMakewithdrawalStatus(Number(makewithdrawal.status)))
        }
    } catch (err:any) {
        console.dir("Erro 401+")
        dispatch(setWithdrawalStatus(false));
        dispatch(setMakewithdrawalStatus(401))
        dispatch(setModalSignInUp(modalTypeEnum.deviceLogin));
    }
}

// NPOS